'use client';

import * as TabsPrimitive from '@radix-ui/react-tabs';
import * as React from 'react';

import { cn } from '../../lib/cn';

/** Radice del gruppo di tab: gestisce quale {@link TabsContent} è visibile. */
const Tabs = TabsPrimitive.Root;

export interface TabsListProps extends React.ComponentPropsWithoutRef<typeof TabsPrimitive.List> {
	/** Se true, permette ai tab di andare a capo invece di restare su una riga sola. */
	wrap?: boolean;
}

/** Barra dei bottoni tab. */
const TabsList = React.forwardRef<React.ElementRef<typeof TabsPrimitive.List>, TabsListProps>(
	({ className, wrap, ...props }, ref) => (
		<TabsPrimitive.List
			ref={ref}
			className={cn(
				'items-center rounded-[9px] border border-border bg-card p-[3px] text-muted-foreground',
				// Senza `wrap` la barra sta su una riga e si limita al contenuto
				// (`inline-flex`). Con `wrap` deve invece restare larga quanto la card
				// sotto anche quando i tab non la riempirebbero da soli (`w-full`), con
				// lo spazio in più distribuito fra i tab (`justify-between`) invece che
				// lasciato in coda: `justify-center` centrava ogni riga per conto suo,
				// lasciando solo e centrato l'ultimo tab su un numero dispari.
				wrap ? 'flex w-full flex-wrap justify-between' : 'inline-flex justify-center',
				className,
			)}
			{...props}
		/>
	),
);
TabsList.displayName = TabsPrimitive.List.displayName;

/** Props di {@link TabsTrigger}: eredita gli attributi standard di Radix (`value`, ecc.). */
export interface TabsTriggerProps extends React.ComponentPropsWithoutRef<
	typeof TabsPrimitive.Trigger
> {}

/** Bottone che attiva un {@link TabsContent} con lo stesso `value`. */
const TabsTrigger = React.forwardRef<
	React.ElementRef<typeof TabsPrimitive.Trigger>,
	TabsTriggerProps
>(({ className, ...props }, ref) => (
	<TabsPrimitive.Trigger
		ref={ref}
		className={cn(
			'inline-flex items-center justify-center whitespace-nowrap rounded-[7px] px-3 py-[5px] text-[13px] font-semibold ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-accent/[0.12] data-[state=active]:text-accent data-[state=inactive]:text-muted-foreground data-[state=inactive]:hover:text-accent',
			className,
		)}
		{...props}
	/>
));
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

/** Props di {@link TabsContent}: eredita gli attributi standard di Radix (`value`, ecc.). */
export interface TabsContentProps extends React.ComponentPropsWithoutRef<
	typeof TabsPrimitive.Content
> {}

/** Contenuto mostrato quando il {@link TabsTrigger} con lo stesso `value` è attivo. */
const TabsContent = React.forwardRef<
	React.ElementRef<typeof TabsPrimitive.Content>,
	TabsContentProps
>(({ className, ...props }, ref) => (
	<TabsPrimitive.Content
		ref={ref}
		className={cn(
			// `forceMount` (usato per le schede dei punti di misura in member-details)
			// tiene il contenuto montato anche da inattivo: Radix imposta `hidden`
			// solo quando NON c'è forceMount, quindi qui va nascosto via CSS sullo
			// stato, altrimenti resta impilato sotto la scheda attiva.
			'mt-3 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 data-[state=inactive]:hidden',
			className,
		)}
		{...props}
	/>
));
TabsContent.displayName = TabsPrimitive.Content.displayName;

export { Tabs, TabsList, TabsTrigger, TabsContent };
